import React, { useState } from 'react';
import { useWeb3 } from '../../contexts/Web3Context';
import UserPathGuide from './UserPathGuide';
import WalletConnection from './WalletConnection';
import CopyrightManager from './CopyrightManager';
import CopyrightTransferManager from './CopyrightTransfer';
import SmartContractManager from './SmartContractManager';
import NFTMarketplace from './NFTMarketplace';
import NFTAuction from './NFTAuction';
import DAOGovernance from './DAOGovernance';
import TokenEconomy from './TokenEconomy';
import RevenueAnalytics from './RevenueAnalytics';
import MusicWorkNFTDisplay from './MusicWorkNFTDisplay';
import { 
  ShieldCheckIcon, 
  CurrencyDollarIcon, 
  PhotoIcon, 
  UsersIcon,
  UserGroupIcon,
  BanknotesIcon,
  FireIcon,
  CubeIcon,
  WalletIcon,
  DocumentCheckIcon,
  MusicalNoteIcon,
  ArrowRightIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

const BlockchainDashboard: React.FC = () => {
  const { wallet } = useWeb3();
  const [activeTab, setActiveTab] = useState<'wallet' | 'copyright' | 'transfer' | 'contracts' | 'nft' | 'auction' | 'dao' | 'token' | 'analytics' | 'works'>('wallet');
  const [selectedUserPath, setSelectedUserPath] = useState<any>(null);
  const [showPathGuide, setShowPathGuide] = useState(true);

  // 添加自定义滚动条样式
  const scrollbarHideStyle = {
    scrollbarWidth: 'none' as const,
    msOverflowStyle: 'none' as const,
    WebkitScrollbar: {
      display: 'none'
    }
  };

  const tabs = [
    { key: 'wallet', label: '钱包', icon: WalletIcon },
    { key: 'copyright', label: '版权管理', icon: DocumentCheckIcon },
    { key: 'transfer', label: '版权转让', icon: ArrowRightIcon },
    { key: 'contracts', label: '智能合约', icon: CurrencyDollarIcon },
    { key: 'works', label: '作品展示', icon: MusicalNoteIcon },
    { key: 'analytics', label: '收益分析', icon: ChartBarIcon },
    { key: 'nft', label: 'NFT 市场', icon: PhotoIcon },
    { key: 'auction', label: 'NFT 拍卖', icon: FireIcon },
    { key: 'dao', label: 'DAO 治理', icon: UserGroupIcon },
    { key: 'token', label: '代币经济', icon: CubeIcon }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'wallet':
        return <WalletConnection />;
      case 'copyright':
        return <CopyrightManager />;
      case 'transfer':
        return <CopyrightTransferManager />;
      case 'contracts':
        return <SmartContractManager />;
      case 'works':
        return <MusicWorkNFTDisplay />;
      case 'analytics':
        return <RevenueAnalytics />;
      case 'nft':
        return <NFTMarketplace />;
      case 'auction':
        return <NFTAuction />;
      case 'dao':
        return <DAOGovernance />;
      case 'token':
        return <TokenEconomy />;
      default:
        return <WalletConnection />;
    }
  };

  const handleSelectPath = (path: any) => {
    setSelectedUserPath(path);
    // 自动导航到路径的第一步
    if (path.steps.length > 0) {
      setActiveTab(path.steps[0].tabKey);
    }
  };

  const handleNavigateToStep = (tabKey: string) => {
    setActiveTab(tabKey as any);
  };

  const togglePathGuide = () => {
    setShowPathGuide(!showPathGuide);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 页面标题 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">区块链中心</h1>
              <p className="text-gray-600">管理您的数字资产和区块链功能</p>
            </div>
            <button
              onClick={togglePathGuide}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
            >
              {showPathGuide ? '隐藏引导' : '显示引导'}
            </button>
          </div>
        </div>

        {/* 用户路径引导 */}
        {showPathGuide && (
          <div className="mb-8">
            <UserPathGuide
              onSelectPath={handleSelectPath}
              onNavigateToStep={handleNavigateToStep}
              currentTab={activeTab}
            />
          </div>
        )}

        {/* 连接状态指示器 */}
        {wallet?.isConnected && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-green-800 font-medium">钱包已连接</span>
              <span className="text-green-600 text-sm">
                {wallet.address.substring(0, 10)}...{wallet.address.substring(wallet.address.length - 8)}
              </span>
              <span className="text-green-600 text-sm">({wallet.network})</span>
            </div>
          </div>
        )}

        {/* 标签页导航 */}
        <div className="mb-8">
          <div className="bg-white rounded-lg shadow-sm border relative">
            {/* 滚动容器 */}
            <div className="overflow-x-auto" style={scrollbarHideStyle}>
              <div className="flex min-w-max">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as any)}
                    className={`flex items-center gap-2 px-3 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap min-w-0 ${
                      activeTab === tab.key
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* 移动端提示 */}
            <div className="md:hidden absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs pointer-events-none">
              →
            </div>
          </div>
          
        </div>

        {/* 内容区域 */}
        <div className="min-h-96">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default BlockchainDashboard;
